import { apiClient } from '@/services/authApi';
import { formatFileSize } from '@/services/documentApi';
import { endpoints } from '@/services/endpoints';
import {
  DeclarationKind,
  DRAFT_COPY_STATUS,
  DraftCopyStatus,
  TaxFilingDocument,
} from '@/types/taxFilings';

interface ApiDoc {
  _id?: string;
  id?: string;
  name: string;
  type?: string;
  size?: string;
  createdAt?: string;
  lastModifiedBy?: string;
  status?: string;
  rejectionComment?: string;
  kind?: string;
}

interface ListResponse {
  files?: ApiDoc[];
}

interface UploadUrlResponse {
  name?: string;
  key?: string;
  uploadUrl: string;
  _id?: string;
}

interface PreviewResponse {
  url: string;
}

function parseSize(sizeStr: string | undefined): number {
  if (!sizeStr) return 0;
  const num = parseFloat(sizeStr);
  if (sizeStr.toUpperCase().includes('KB')) return num * 1024;
  if (sizeStr.toUpperCase().includes('MB')) return num * 1024 * 1024;
  if (sizeStr.toUpperCase().includes('GB')) return num * 1024 * 1024 * 1024;
  return num;
}

function mapDoc(f: ApiDoc): TaxFilingDocument {
  return {
    id: f._id ?? f.id ?? '',
    name: f.name,
    type: f.type,
    size: parseSize(f.size),
    createdAt: f.createdAt,
    lastModifiedBy: f.lastModifiedBy,
    status: f.status as DraftCopyStatus | undefined,
    rejectionComment: f.rejectionComment,
    kind: f.kind as DeclarationKind | undefined,
  };
}

async function listFrom(
  path: string,
  params: Record<string, string | number>
): Promise<TaxFilingDocument[]> {
  const response = await apiClient.get<ListResponse | ApiDoc[]>(path, { params });
  const data = response.data;
  const raw = Array.isArray(data) ? data : data?.files ?? [];
  return raw.map(mapDoc);
}

function resolveDocumentId(urlInfo: UploadUrlResponse): string {
  if (urlInfo._id) return urlInfo._id;
  const parts = urlInfo.key?.split('/') ?? [];
  return parts[parts.length - 1] ?? '';
}

async function confirmUpload(
  confirmPath: string,
  params?: Record<string, string>
): Promise<void> {
  await apiClient.post(confirmPath, {}, { params });
}

async function failUpload(
  failPath: string,
  params?: Record<string, string>
): Promise<void> {
  try {
    await apiClient.post(failPath, {}, { params });
  } catch {
    // Best-effort cleanup
  }
}

async function uploadTo(
  path: string,
  file: File,
  body: Record<string, string | number>,
  confirmPath: (documentId: string) => string,
  failPath: (documentId: string) => string,
  params?: Record<string, string>,
  onProgress?: (progress: number, status: 'uploading' | 'success' | 'error') => void
): Promise<TaxFilingDocument> {
  const response = await apiClient.post<UploadUrlResponse>(path, body, { params });
  const urlInfo = response.data;
  if (!urlInfo?.uploadUrl) {
    onProgress?.(0, 'error');
    throw new Error('No upload URL received');
  }
  const documentId = resolveDocumentId(urlInfo);
  const contentType = file.type || 'application/octet-stream';
  try {
    onProgress?.(0, 'uploading');
    const res = await fetch(urlInfo.uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': contentType },
    });
    if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
    if (documentId) {
      await confirmUpload(confirmPath(documentId), params);
    }
    onProgress?.(100, 'success');
    return mapDoc({
      _id: documentId,
      name: urlInfo.name ?? file.name,
      type: contentType,
      size: String(file.size),
      status: DRAFT_COPY_STATUS.PENDING_CLIENT,
    });
  } catch (err) {
    if (documentId) {
      await failUpload(failPath(documentId), params);
    }
    onProgress?.(0, 'error');
    throw err instanceof Error ? err : new Error('Failed to upload file');
  }
}

async function previewFrom(path: string, params?: Record<string, string>) {
  const response = await apiClient.get<PreviewResponse>(path, { params });
  return response.data?.url ?? '';
}

function tenantParams(tenantId?: string | null) {
  return tenantId != null && tenantId !== '' ? { tenantId } : undefined;
}

function fileBody(file: File, taxYear: number, extra: Record<string, string> = {}) {
  return {
    name: file.name,
    type: file.type || 'application/octet-stream',
    size: formatFileSize(file.size),
    taxYear,
    ...extra,
  };
}

// ---- User ----

export const listMyTaxSummaries = (taxYear: number) =>
  listFrom(endpoints.taxSummaries.list, { taxYear });

export const listMyDraftCopies = (taxYear: number) =>
  listFrom(endpoints.draftCopies.list, { taxYear });

export const listMyDeclarations = (taxYear: number, kind?: DeclarationKind) =>
  listFrom(endpoints.declarations.list, {
    taxYear,
    ...(kind ? { kind } : {}),
  });

export const listMyFinalCopies = (taxYear: number) =>
  listFrom(endpoints.finalCopies.list, { taxYear });

export const getMyTaxSummaryPreview = (id: string) =>
  previewFrom(endpoints.taxSummaries.getPreview(id));

export const getMyDraftCopyPreview = (id: string) =>
  previewFrom(endpoints.draftCopies.getPreview(id));

export const getMyDeclarationPreview = (id: string) =>
  previewFrom(endpoints.declarations.getPreview(id));

export const getMyFinalCopyPreview = (id: string) =>
  previewFrom(endpoints.finalCopies.getPreview(id));

export const updateMyDraftCopyStatus = (
  id: string,
  payload: { status: DraftCopyStatus; rejectionComment?: string }
) => apiClient.put(endpoints.draftCopies.update(id), payload);

export const updateMyTaxSummaryStatus = (
  id: string,
  payload: { status: DraftCopyStatus; rejectionComment?: string }
) => apiClient.put(endpoints.taxSummaries.update(id), payload);

export const updateMyFinalCopyStatus = (
  id: string,
  payload: { status: DraftCopyStatus; rejectionComment?: string }
) => apiClient.put(endpoints.finalCopies.update(id), payload);

export const uploadMySignedDeclaration = (
  file: File,
  taxYear: number,
  onProgress?: (progress: number, status: 'uploading' | 'success' | 'error') => void
) =>
  uploadTo(
    endpoints.declarations.upload,
    file,
    fileBody(file, taxYear),
    endpoints.declarations.confirmUpload,
    endpoints.declarations.failUpload,
    undefined,
    onProgress
  );

// ---- Admin ----

export const listTaxSummariesForUser = (
  userId: string,
  options: { taxYear: number; tenantId?: string | null }
) =>
  listFrom(endpoints.adminTaxSummaries.listByUser(userId), {
    taxYear: options.taxYear,
    ...(options.tenantId ? { tenantId: options.tenantId } : {}),
  });

export const listDraftCopiesForUser = (
  userId: string,
  options: { taxYear: number; tenantId?: string | null }
) =>
  listFrom(endpoints.adminDraftCopies.listByUser(userId), {
    taxYear: options.taxYear,
    ...(options.tenantId ? { tenantId: options.tenantId } : {}),
  });

export const listDeclarationsForUser = (
  userId: string,
  options: { taxYear: number; tenantId?: string | null; kind?: DeclarationKind }
) =>
  listFrom(endpoints.adminDeclarations.listByUser(userId), {
    taxYear: options.taxYear,
    ...(options.tenantId ? { tenantId: options.tenantId } : {}),
    ...(options.kind ? { kind: options.kind } : {}),
  });

export const listFinalCopiesForUser = (
  userId: string,
  options: { taxYear: number; tenantId?: string | null }
) =>
  listFrom(endpoints.adminFinalCopies.listByUser(userId), {
    taxYear: options.taxYear,
    ...(options.tenantId ? { tenantId: options.tenantId } : {}),
  });

export const createTaxSummaryForUser = (
  userId: string,
  file: File,
  options: { taxYear: number; tenantId?: string | null },
  onProgress?: (progress: number, status: 'uploading' | 'success' | 'error') => void
) =>
  uploadTo(
    endpoints.adminTaxSummaries.createForUser(userId),
    file,
    fileBody(file, options.taxYear),
    (id) => endpoints.adminTaxSummaries.confirmUpload(userId, id),
    (id) => endpoints.adminTaxSummaries.failUpload(userId, id),
    tenantParams(options.tenantId),
    onProgress
  );

export const createDraftCopyForUser = (
  userId: string,
  file: File,
  options: { taxYear: number; tenantId?: string | null },
  onProgress?: (progress: number, status: 'uploading' | 'success' | 'error') => void
) =>
  uploadTo(
    endpoints.adminDraftCopies.createForUser(userId),
    file,
    fileBody(file, options.taxYear),
    (id) => endpoints.adminDraftCopies.confirmUpload(userId, id),
    (id) => endpoints.adminDraftCopies.failUpload(userId, id),
    tenantParams(options.tenantId),
    onProgress
  );

export const createDeclarationForUser = (
  userId: string,
  file: File,
  kind: DeclarationKind,
  options: { taxYear: number; tenantId?: string | null },
  onProgress?: (progress: number, status: 'uploading' | 'success' | 'error') => void
) =>
  uploadTo(
    endpoints.adminDeclarations.createForUser(userId),
    file,
    fileBody(file, options.taxYear, { kind }),
    (id) => endpoints.adminDeclarations.confirmUpload(userId, id),
    (id) => endpoints.adminDeclarations.failUpload(userId, id),
    tenantParams(options.tenantId),
    onProgress
  );

export const createFinalCopyForUser = (
  userId: string,
  file: File,
  options: { taxYear: number; tenantId?: string | null },
  onProgress?: (progress: number, status: 'uploading' | 'success' | 'error') => void
) =>
  uploadTo(
    endpoints.adminFinalCopies.createForUser(userId),
    file,
    fileBody(file, options.taxYear),
    (id) => endpoints.adminFinalCopies.confirmUpload(userId, id),
    (id) => endpoints.adminFinalCopies.failUpload(userId, id),
    tenantParams(options.tenantId),
    onProgress
  );

export const getTaxSummaryPreviewAdmin = (
  userId: string,
  id: string,
  tenantId?: string | null
) =>
  previewFrom(
    endpoints.adminTaxSummaries.getPreview(userId, id),
    tenantParams(tenantId)
  );

export const getDraftCopyPreviewAdmin = (
  userId: string,
  id: string,
  tenantId?: string | null
) =>
  previewFrom(
    endpoints.adminDraftCopies.getPreview(userId, id),
    tenantParams(tenantId)
  );

export const getDeclarationPreviewAdmin = (
  userId: string,
  id: string,
  tenantId?: string | null
) =>
  previewFrom(
    endpoints.adminDeclarations.getPreview(userId, id),
    tenantParams(tenantId)
  );

export const getFinalCopyPreviewAdmin = (
  userId: string,
  id: string,
  tenantId?: string | null
) =>
  previewFrom(
    endpoints.adminFinalCopies.getPreview(userId, id),
    tenantParams(tenantId)
  );

export const updateDraftCopyStatusForUser = (
  userId: string,
  id: string,
  payload: { status: DraftCopyStatus; rejectionComment?: string },
  tenantId?: string | null
) =>
  apiClient.put(endpoints.adminDraftCopies.update(userId, id), payload, {
    params: tenantParams(tenantId),
  });

export const updateTaxSummaryStatusForUser = (
  userId: string,
  id: string,
  payload: { status: DraftCopyStatus; rejectionComment?: string },
  tenantId?: string | null
) =>
  apiClient.put(endpoints.adminTaxSummaries.update(userId, id), payload, {
    params: tenantParams(tenantId),
  });

export const updateFinalCopyStatusForUser = (
  userId: string,
  id: string,
  payload: { status: DraftCopyStatus; rejectionComment?: string },
  tenantId?: string | null
) =>
  apiClient.put(endpoints.adminFinalCopies.update(userId, id), payload, {
    params: tenantParams(tenantId),
  });

export async function getTaxFilingUploadEmailSettings(): Promise<{
  enabled: boolean;
}> {
  const response = await apiClient.get<{ enabled: boolean }>(
    endpoints.settings.taxFilingUploadEmails
  );
  return response.data;
}

export async function updateTaxFilingUploadEmailSettings(enabled: boolean) {
  const response = await apiClient.put<{ enabled: boolean }>(
    endpoints.settings.taxFilingUploadEmails,
    { enabled }
  );
  return response.data;
}

export async function downloadFromPreviewUrl(url: string, fileName: string) {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  link.click();
}
